import { paymentSessionService } from './paymentSessionService';

class PaymentStatusService {
  private statusCheckInterval = 5000;
  private statusListeners: Map<string, (status: string) => void>;
  private intervals: Map<string, ReturnType<typeof setInterval>>;

  constructor() {
    this.statusListeners = new Map();
    this.intervals = new Map();
  }

  private getStorageKey(token: string): string {
    return `payment_status_${token}`;
  }

  private getStoredStatus(token: string): string {
    const status = localStorage.getItem(this.getStorageKey(token));
    return this.normalizeStatus(status || 'pending');
  }

  private setStoredStatus(token: string, status: string) {
    localStorage.setItem(this.getStorageKey(token), this.normalizeStatus(status));
  }

  private handleStatusUpdate(token: string, status: string) {
    const normalizedStatus = this.normalizeStatus(status);
    this.setStoredStatus(token, normalizedStatus);

    const listener = this.statusListeners.get(token);
    if (listener) {
      listener(normalizedStatus);
    }
  }

  private normalizeStatus(status: string): string {
    if (!status) {
      return 'pending';
    }

    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'cancel':
      case 'canceled':
        return 'canceled';
      case 'completed':
      case 'authorized':
      case 'shipping':
      case 'paid':
        return 'paid';
      case 'fail':
      case 'failed':
      case 'expired':
        return 'failed';
      default:
        return 'pending';
    }
  }

  private async pollStatus(token: string) {
    await this.refreshStatus(token);
  }

  private async fetchProviderStatus(token: string): Promise<string | null> {
    const session = paymentSessionService.getSession(token);

    try {
      const endpointCandidates = [
        `/api/checkout/token-status?token=${encodeURIComponent(token)}`,
        ...(session
          ? [
              `/api/payment-status?${new URLSearchParams({
                kind: session.providerType,
                id: session.providerId,
              }).toString()}`,
              `/paymentStatus.php?${new URLSearchParams({
                kind: session.providerType,
                id: session.providerId,
              }).toString()}`,
            ]
          : []),
      ];
      let lastError = 'Impossible de verifier le statut du paiement';

      for (const endpoint of endpointCandidates) {
        try {
          const response = await fetch(endpoint);
          const rawText = await response.text();
          let payload: { status?: string; error?: string } | null = null;

          if (rawText) {
            try {
              payload = JSON.parse(rawText);
            } catch {
              payload = null;
            }
          }

          if (!response.ok) {
            lastError = payload?.error || lastError;
            continue;
          }

          if (!payload?.status) {
            lastError = 'Reponse de statut Mollie invalide';
            continue;
          }

          return this.normalizeStatus(payload.status);
        } catch {
          // Try next endpoint candidate.
        }
      }

      throw new Error(lastError);
    } catch (error) {
      console.error('Error fetching Mollie payment status:', error);
      return null;
    }
  }

  public async refreshStatus(token: string): Promise<string> {
    const remoteStatus = await this.fetchProviderStatus(token);
    const nextStatus = remoteStatus || this.getStoredStatus(token);

    this.handleStatusUpdate(token, nextStatus);

    if (nextStatus !== 'pending') {
      this.unsubscribeFromStatus(token);
    }

    return nextStatus;
  }

  public async updateStatus(token: string, status: string): Promise<string> {
    const normalizedStatus = this.normalizeStatus(status);
    this.handleStatusUpdate(token, normalizedStatus);
    return normalizedStatus;
  }

  public async checkInitialStatus(token: string): Promise<string> {
    return this.refreshStatus(token);
  }

  public subscribeToStatus(token: string, callback: (status: string) => void) {
    this.unsubscribeFromStatus(token);
    this.statusListeners.set(token, callback);

    const interval = setInterval(() => {
      this.pollStatus(token).catch((error) => {
        console.error('Error polling status:', error);
      });
    }, this.statusCheckInterval);

    this.intervals.set(token, interval);
  }

  public unsubscribeFromStatus(token: string) {
    this.statusListeners.delete(token);

    const interval = this.intervals.get(token);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(token);
    }
  }
}

export const paymentStatusService = new PaymentStatusService();
